import axios from 'axios';
import { AUTH_CONFIG, API_BASE_URL, TENANT_ID } from '@/config/constants';
import { ENDPOINTS, getEndpointPath, isValidTenantId } from '@/api/endpoints';
import { applyAccessTokenClaims, persistUser } from '../access/teamLeaderAccess';

const EXTRA_KEYS = [
  'tenantId',
  'refreshToken',
  'orgId',
  'companyName',
  'support_impersonation',
  'support_impersonated_by',
  'support_impersonation_exp',
  'support_impersonation_session_id',
  'support_impersonation_target',
  'employeeID',
];

/** Refresh this long before JWT expiry so users never hit a 401 window. */
const REFRESH_LEAD_MS = 2 * 60 * 1000;
const MIN_SCHEDULE_MS = 15_000;
const ACCESS_SKEW_MS = 60_000;

let inFlightRefresh = null;
let refreshTimer = null;
let memoryRefreshToken = null;
const tokenRefreshListeners = new Set();

const AUTH_REQUEST_CONFIG = { withCredentials: true };

function forgetPersistedRefreshToken() {
  localStorage.removeItem(AUTH_CONFIG.REFRESH_TOKEN_STORAGE_KEY);
  localStorage.removeItem('refreshToken');
}

function getTenantId() {
  const stored = localStorage.getItem(AUTH_CONFIG.TENANT_ID_STORAGE_KEY);
  return isValidTenantId(stored) ? stored : TENANT_ID;
}

function getStoredAccessToken() {
  return localStorage.getItem(AUTH_CONFIG.TOKEN_STORAGE_KEY);
}

function getTokenExpiryMs(token) {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch (_) {
    return null;
  }
}

function clearRefreshSchedule() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function notifyTokenRefreshed(token) {
  tokenRefreshListeners.forEach((callback) => {
    try {
      callback(token);
    } catch (error) {
      console.warn('Token refresh listener error:', error);
    }
  });
}

export function onTokenRefreshed(callback) {
  tokenRefreshListeners.add(callback);
  return () => tokenRefreshListeners.delete(callback);
}

export function persistTokens({ token, refreshToken, tenantId }) {
  if (token) {
    localStorage.setItem(AUTH_CONFIG.TOKEN_STORAGE_KEY, token);
  }
  if (refreshToken) {
    memoryRefreshToken = refreshToken;
  }
  // Refresh lives in an HttpOnly cookie. Never persist it for JS to read.
  forgetPersistedRefreshToken();
  if (isValidTenantId(tenantId)) {
    localStorage.setItem(AUTH_CONFIG.TENANT_ID_STORAGE_KEY, tenantId);
  }
  if (token) {
    scheduleTokenRefresh();
  }
}

export function getRefreshToken() {
  if (memoryRefreshToken) return memoryRefreshToken;
  // One-time lift of a leftover localStorage token from older clients.
  const leftover =
    localStorage.getItem(AUTH_CONFIG.REFRESH_TOKEN_STORAGE_KEY) ||
    localStorage.getItem('refreshToken');
  if (leftover) {
    memoryRefreshToken = leftover;
    forgetPersistedRefreshToken();
    return leftover;
  }
  return null;
}

export function clearAuthStorage() {
  clearRefreshSchedule();
  memoryRefreshToken = null;
  localStorage.removeItem(AUTH_CONFIG.TOKEN_STORAGE_KEY);
  localStorage.removeItem(AUTH_CONFIG.USER_STORAGE_KEY);
  localStorage.removeItem(AUTH_CONFIG.REFRESH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(AUTH_CONFIG.EMPLOYEE_ID_STORAGE_KEY);
  localStorage.removeItem(AUTH_CONFIG.TENANT_ID_STORAGE_KEY);
  EXTRA_KEYS.forEach((key) => localStorage.removeItem(key));
}

export function isAccessTokenFresh(token, skewMs = 0) {
  if (!token) return false;
  const expMs = getTokenExpiryMs(token);
  if (!expMs) return false;
  return expMs > Date.now() + skewMs;
}

/**
 * Schedules a silent background refresh before the access token expires.
 * Keeps localStorage in sync so raw fetch() callers also get a valid token.
 */
export function scheduleTokenRefresh() {
  clearRefreshSchedule();

  const token = getStoredAccessToken();
  if (!token) return;

  const expMs = getTokenExpiryMs(token);
  if (!expMs) return;

  const delay = Math.max(MIN_SCHEDULE_MS, expMs - Date.now() - REFRESH_LEAD_MS);

  refreshTimer = setTimeout(async () => {
    refreshTimer = null;
    try {
      await refreshSession();
    } catch (_) {
      // Next user action or scheduler cycle will retry; avoid logout on idle tabs.
    }
  }, delay);
}

export function stopTokenRefreshScheduler() {
  clearRefreshSchedule();
}

async function doRefresh() {
  const refreshToken = getRefreshToken();
  const tenantId = getTenantId();
  const response = await axios.post(
    `${API_BASE_URL}${getEndpointPath(ENDPOINTS.AUTH.REFRESH)}`,
    refreshToken ? { refreshToken } : {},
    {
      ...AUTH_REQUEST_CONFIG,
      headers: {
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
      },
    }
  );

  const next = response.data?.data || {};
  if (!next.token) {
    throw new Error('REFRESH_FAILED');
  }

  persistTokens({
    token: next.token,
    refreshToken: next.refreshToken,
    tenantId: next.tenantId,
  });
  try {
    const savedUser = localStorage.getItem(AUTH_CONFIG.USER_STORAGE_KEY);
    if (savedUser) {
      persistUser(applyAccessTokenClaims(JSON.parse(savedUser), next.token));
    }
  } catch (_) {
    // Session flags are best-effort; a later restore will re-read the token.
  }
  notifyTokenRefreshed(next.token);
  return next.token;
}

export function refreshSession() {
  if (!inFlightRefresh) {
    inFlightRefresh = doRefresh().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

export async function ensureFreshAccessToken() {
  const token = getStoredAccessToken();
  if (isAccessTokenFresh(token, ACCESS_SKEW_MS)) {
    return token;
  }

  try {
    return await refreshSession();
  } catch {
    return token;
  }
}

/** Returns the current access token (kept fresh by the background scheduler). */
export function getAccessToken() {
  return getStoredAccessToken();
}
